import type { Connecting } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Callout, Icon } from '@beyond-js/ui/react';
import { Card } from '@accounts/ui';

/**
 * Who receives access, as one line from where the answer goes to the product and the person it acts for:
 * the app's address (said as this computer for a loopback redirect, with its warning under the line), a
 * hairline toward the product, and the person. The app's name is the page's title, so it is not repeated
 * here; that it chose that name itself is said once, under the line.
 */
export function Route({ asked }: { asked: Connecting }) {
	const { text } = usePresence();
	const { host, loopback } = asked.client.redirect;
	const person = asked.person.name ?? text('connect.person.unnamed');
	return (
		<section className="flex flex-col gap-2" aria-label={text('permissions.route')}>
			<Card>
				<div className="permission-route">
					<span className="permission-route-end">
						<Icon name="plug" size={20} />
						<span className="min-w-0 text-small text-ink">
							<Hosted words={text(loopback ? 'permissions.route.local' : 'permissions.route.answers', { host: '\u0000' })} host={host} />
						</span>
					</span>
					<span className="permission-route-line" aria-hidden="true">
						<Icon name="right" size={16} />
					</span>
					<span className="permission-route-end permission-route-target">
						<span className="text-body font-semibold text-ink">{asked.product.name}</span>
						<span className="text-small text-ink-muted">{text('permissions.route.for', { person })}</span>
					</span>
				</div>
			</Card>
			<p className="max-w-reading text-small text-ink-muted">{text('connect.unverified.detail')}</p>
			{loopback ? <Callout tone="warning" title={text('connect.local')} body={text('connect.local.detail', { host })} /> : null}
		</section>
	);
}

/** A sentence with the address in it, the address alone in the monospace face for identifiers */
function Hosted({ words, host }: { words: string; host: string }) {
	const [before, after = ''] = words.split('\u0000');
	return (
		<>
			{before}
			<span className="break-all font-mono">{host}</span>
			{after}
		</>
	);
}
